import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  FileText, AlertTriangle, Shield, Send, Calendar, Clock,
  Plus, Edit2, Trash2, Eye, Download, RefreshCw, CheckCircle,
  XCircle, Upload, Building2, User, FileWarning, Mail, 
  Loader2, BarChart3, Archive, Play
} from 'lucide-react';

const REPORT_TYPES = [
  { 
    id: 'ROS', 
    nombre: 'Reporte de Operación Sospechosa', 
    descripcion: 'Reporte obligatorio ante la UIF',
    icon: AlertTriangle,
    color: '#dc2626'
  },
  { 
    id: 'SAR', 
    nombre: 'Suspicious Activity Report', 
    descripcion: 'Reporte para entidades internacionales',
    icon: Shield,
    color: '#7c3aed'
  },
  { 
    id: 'CTR', 
    nombre: 'Currency Transaction Report', 
    descripcion: 'Reporte de transacciones de divisas',
    icon: Building2,
    color: '#0891b2'
  },
  { 
    id: 'STR', 
    nombre: 'Suspicious Transaction Report', 
    descripcion: 'Reporte de transacción sospechosa local',
    icon: FileWarning,
    color: '#ea580c'
  }
];

const STATUS_BADGES = {
  BORRADOR: { color: '#64748b', bg: '#f1f5f9', label: 'Borrador' },
  EN_REVISION: { color: '#f59e0b', bg: '#fef3c7', label: 'En Revisión' },
  APROBADO: { color: '#22c55e', bg: '#dcfce7', label: 'Aprobado' },
  ENVIADO: { color: '#3b82f6', bg: '#dbeafe', label: 'Enviado' },
  RECHAZADO: { color: '#ef4444', bg: '#fee2e2', label: 'Rechazado' }
};

const ReportesRegulatorios = () => {
  const [activeTab, setActiveTab] = useState('reportes');
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    tipo: '',
    estado: '',
    search: ''
  });
  
  // Form state
  const [reportForm, setReportForm] = useState({
    tipo: 'ROS',
    caso_id: '',
    titulo: '',
    descripcion: '',
    monto_total: '',
    fecha_operacion: '',
    sujeto_obligado: '',
    personas_involucradas: []
  });
  
  const [scheduleForm, setScheduleForm] = useState({
    nombre: '',
    tipo_reporte: 'compliance',
    frecuencia: 'semanal',
    dia_envio: '1',
    hora_envio: '09:00',
    emails: '',
    activo: true
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [reportsRes, scheduledRes, statsRes] = await Promise.all([
        api.get('/regulatory-reports', { params: filters }),
        api.get('/regulatory-reports/scheduled'),
        api.get('/regulatory-reports/stats')
      ]);
      
      setReports(reportsRes.data.reports || []);
      setScheduledReports(scheduledRes.data.scheduled || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      // Use mock data
      setReports(generateMockReports());
      setScheduledReports(generateMockScheduled());
      setStats(generateMockStats());
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const generateMockReports = () => [
    {
      id: 'REP-001',
      tipo: 'ROS',
      titulo: 'Operación sospechosa - Estructuración',
      estado: 'EN_REVISION',
      caso_id: 'CASO-2024-0045',
      monto_total: 5000000,
      fecha_creacion: '2024-03-15T10:30:00Z',
      fecha_operacion: '2024-03-10',
      creado_por: 'Juan Analista'
    },
    {
      id: 'REP-002',
      tipo: 'SAR',
      titulo: 'Actividad inusual - Transferencias internacionales',
      estado: 'APROBADO',
      caso_id: 'CASO-2024-0038',
      monto_total: 12500000,
      fecha_creacion: '2024-03-10T14:20:00Z',
      fecha_operacion: '2024-03-05',
      creado_por: 'María Compliance'
    },
    {
      id: 'REP-003',
      tipo: 'ROS',
      titulo: 'Operación sospechosa - Red de empresas',
      estado: 'ENVIADO',
      caso_id: 'CASO-2024-0032',
      monto_total: 25000000,
      fecha_creacion: '2024-03-01T09:15:00Z',
      fecha_operacion: '2024-02-28',
      fecha_envio: '2024-03-02T11:00:00Z',
      creado_por: 'Pedro Supervisor'
    }
  ];

  const generateMockScheduled = () => [
    {
      id: 'SCH-001',
      nombre: 'Reporte Semanal Compliance',
      tipo_reporte: 'compliance',
      frecuencia: 'semanal',
      emails: ['compliance@empresa.com', 'gerencia@empresa.com'],
      activo: true,
      ultimo_envio: '2024-03-15T09:00:00Z',
      proximo_envio: '2024-03-22T09:00:00Z'
    },
    {
      id: 'SCH-002',
      nombre: 'Reporte Mensual Ejecutivo',
      tipo_reporte: 'executive',
      frecuencia: 'mensual',
      emails: ['direccion@empresa.com'],
      activo: true,
      ultimo_envio: '2024-03-01T08:00:00Z',
      proximo_envio: '2024-04-01T08:00:00Z'
    }
  ];

  const generateMockStats = () => ({
    total_reportes: 45,
    por_tipo: { ROS: 25, SAR: 12, CTR: 5, STR: 3 },
    por_estado: { BORRADOR: 5, EN_REVISION: 8, APROBADO: 10, ENVIADO: 22 },
    monto_total_reportado: 150000000,
    ultimo_mes: { generados: 12, enviados: 10 }
  });

  const handleCreateReport = async () => {
    try {
      await api.post('/regulatory-reports', reportForm);
      toast.success('Reporte creado exitosamente');
      setShowCreateDialog(false);
      resetReportForm();
      loadData();
    } catch (error) {
      toast.error('Error al crear reporte');
    }
  };

  const handleSubmitReport = async (reportId) => {
    if (!window.confirm('¿Enviar este reporte a la entidad reguladora?')) return;
    try {
      await api.post(`/regulatory-reports/${reportId}/submit`);
      toast.success('Reporte enviado correctamente');
      loadData();
    } catch (error) {
      toast.error('Error al enviar');
    }
  };

  const handleApproveReport = async (reportId) => {
    try {
      await api.post(`/regulatory-reports/${reportId}/approve`);
      toast.success('Reporte aprobado');
      loadData();
    } catch (error) {
      toast.error('Error al aprobar');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('¿Eliminar este reporte?')) return;
    try {
      await api.delete(`/regulatory-reports/${reportId}`);
      toast.success('Reporte eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleSaveSchedule = async () => {
    try {
      const data = {
        ...scheduleForm,
        emails: scheduleForm.emails.split(',').map(e => e.trim()).filter(e => e)
      };
      await api.post('/regulatory-reports/schedule', data);
      toast.success('Programación guardada');
      setShowScheduleDialog(false);
      resetScheduleForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar programación');
    }
  };

  const handleToggleSchedule = async (scheduleId, activo) => {
    try {
      await api.put(`/regulatory-reports/schedule/${scheduleId}`, { activo: !activo });
      toast.success(activo ? 'Programación desactivada' : 'Programación activada');
      loadData();
    } catch (error) {
      toast.error('Error');
    }
  };

  const resetReportForm = () => {
    setReportForm({
      tipo: 'ROS',
      caso_id: '',
      titulo: '',
      descripcion: '',
      monto_total: '',
      fecha_operacion: '',
      sujeto_obligado: '',
      personas_involucradas: []
    });
  };

  const resetScheduleForm = () => {
    setScheduleForm({
      nombre: '',
      tipo_reporte: 'compliance',
      frecuencia: 'semanal',
      dia_envio: '1',
      hora_envio: '09:00',
      emails: '',
      activo: true
    });
  };

  const getStatusBadge = (estado) => {
    const config = STATUS_BADGES[estado] || STATUS_BADGES.BORRADOR;
    return (
      <Badge style={{ backgroundColor: config.bg, color: config.color }}>
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50" data-testid="regulatory-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText size={28} className="text-red-600" />
            Reportes Regulatorios
          </h1>
          <p className="text-slate-600 mt-1">Gestión de reportes SAR/ROS y envío automatizado</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} style={{ backgroundColor: '#dc2626', color: 'white' }}>
            <Plus size={16} className="mr-2" />
            Nuevo Reporte
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <FileText className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total_reportes}</p>
                <p className="text-sm text-slate-500">Reportes Totales</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Send className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.por_estado?.ENVIADO || 0}</p>
                <p className="text-sm text-slate-500">Enviados</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="text-amber-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats.por_estado?.EN_REVISION || 0}</p>
                <p className="text-sm text-slate-500">En Revisión</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <BarChart3 className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.monto_total_reportado || 0)}</p>
                <p className="text-sm text-slate-500">Monto Reportado</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="reportes" className="data-[state=active]:bg-white">
            <FileText size={16} className="mr-2" />
            Reportes
          </TabsTrigger>
          <TabsTrigger value="programados" className="data-[state=active]:bg-white">
            <Calendar size={16} className="mr-2" />
            Envíos Programados
          </TabsTrigger>
          <TabsTrigger value="plantillas" className="data-[state=active]:bg-white">
            <Archive size={16} className="mr-2" />
            Plantillas
          </TabsTrigger>
        </TabsList>

        {/* Reportes Tab */}
        <TabsContent value="reportes" className="space-y-4">
          {/* Filters */}
          <Card className="p-4 bg-white">
            <div className="flex flex-wrap items-center gap-4">
              <Input
                placeholder="Buscar reportes..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="max-w-xs"
              />
              <Select value={filters.tipo || "all"} onValueChange={(v) => setFilters({ ...filters, tipo: v === "all" ? "" : v })}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {REPORT_TYPES.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.estado || "all"} onValueChange={(v) => setFilters({ ...filters, estado: v === "all" ? "" : v })}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(STATUS_BADGES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Card>

          {/* Reports Table */}
          <Card className="bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Monto</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <RefreshCw className="animate-spin mx-auto text-slate-400" size={24} />
                    </TableCell>
                  </TableRow>
                ) : reports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No hay reportes
                    </TableCell>
                  </TableRow>
                ) : (
                  reports.map(report => {
                    const typeConfig = REPORT_TYPES.find(t => t.id === report.tipo);
                    const Icon = typeConfig?.icon || FileText;
                    return (
                      <TableRow key={report.id}>
                        <TableCell className="font-mono text-sm">{report.id}</TableCell>
                        <TableCell>
                          <Badge style={{ backgroundColor: `${typeConfig?.color}20`, color: typeConfig?.color }}>
                            <Icon size={12} className="mr-1" />
                            {report.tipo}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium max-w-xs truncate">{report.titulo}</TableCell>
                        <TableCell className="font-mono">{formatCurrency(report.monto_total)}</TableCell>
                        <TableCell>{getStatusBadge(report.estado)}</TableCell>
                        <TableCell className="text-sm text-slate-500">{formatDate(report.fecha_creacion)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => { setSelectedReport(report); setShowDetailDialog(true); }}>
                              <Eye size={14} />
                            </Button>
                            {report.estado === 'BORRADOR' && (
                              <Button size="sm" variant="ghost" onClick={() => handleApproveReport(report.id)} className="text-green-600">
                                <CheckCircle size={14} />
                              </Button>
                            )}
                            {report.estado === 'APROBADO' && (
                              <Button size="sm" variant="ghost" onClick={() => handleSubmitReport(report.id)} className="text-blue-600">
                                <Send size={14} />
                              </Button>
                            )}
                            {report.estado === 'BORRADOR' && (
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteReport(report.id)} className="text-red-500">
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Programados Tab */}
        <TabsContent value="programados" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-slate-600">Configure el envío automático de reportes por email</p>
            <Button onClick={() => setShowScheduleDialog(true)}>
              <Plus size={16} className="mr-2" />
              Nueva Programación
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {scheduledReports.map(schedule => (
              <Card key={schedule.id} className="p-4 bg-white">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${schedule.activo ? 'bg-green-100' : 'bg-slate-100'}`}>
                      <Mail className={schedule.activo ? 'text-green-600' : 'text-slate-400'} size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{schedule.nombre}</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Tipo: {schedule.tipo_reporte} • Frecuencia: {schedule.frecuencia}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {schedule.emails.map((email, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{email}</Badge>
                        ))}
                      </div>
                      {schedule.ultimo_envio && (
                        <p className="text-xs text-slate-400 mt-2">
                          Último envío: {formatDate(schedule.ultimo_envio)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={schedule.activo}
                      onCheckedChange={() => handleToggleSchedule(schedule.id, schedule.activo)}
                    />
                  </div>
                </div>
              </Card>
            ))}
            
            {scheduledReports.length === 0 && (
              <Card className="p-8 text-center col-span-2 bg-white">
                <Calendar size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No hay envíos programados</p>
                <Button variant="outline" className="mt-4" onClick={() => setShowScheduleDialog(true)}>
                  <Plus size={16} className="mr-2" />
                  Crear programación
                </Button>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Plantillas Tab */}
        <TabsContent value="plantillas" className="space-y-4">
          <Card className="p-8 text-center bg-white">
            <Archive size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="font-semibold text-slate-900">Plantillas de Reportes</h3>
            <p className="text-slate-500 mt-2">
              Las plantillas permiten agilizar la creación de reportes regulatorios
            </p>
            <div className="flex justify-center gap-2 mt-4">
              {REPORT_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <Button key={type.id} variant="outline" onClick={() => {
                    setReportForm({ ...reportForm, tipo: type.id });
                    setShowCreateDialog(true);
                  }}>
                    <Icon size={16} className="mr-2" style={{ color: type.color }} />
                    {type.id}
                  </Button>
                );
              })}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Report Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="text-red-600" size={20} />
              Nuevo Reporte Regulatorio
            </DialogTitle>
            <DialogDescription>Complete la información del reporte</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-auto">
            <div>
              <Label>Tipo de Reporte *</Label>
              <Select value={reportForm.tipo} onValueChange={(v) => setReportForm({ ...reportForm, tipo: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      <div className="flex items-center gap-2">
                        <t.icon size={16} style={{ color: t.color }} />
                        {t.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Título del Reporte *</Label>
              <Input
                value={reportForm.titulo}
                onChange={(e) => setReportForm({ ...reportForm, titulo: e.target.value })}
                placeholder="Ej: Operación sospechosa - Estructuración"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>ID del Caso (opcional)</Label>
                <Input
                  value={reportForm.caso_id}
                  onChange={(e) => setReportForm({ ...reportForm, caso_id: e.target.value })}
                  placeholder="CASO-2024-XXXX"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Fecha de Operación *</Label>
                <Input
                  type="date"
                  value={reportForm.fecha_operacion}
                  onChange={(e) => setReportForm({ ...reportForm, fecha_operacion: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Monto Total Involucrado</Label>
              <Input
                type="number"
                value={reportForm.monto_total}
                onChange={(e) => setReportForm({ ...reportForm, monto_total: e.target.value })}
                placeholder="0.00"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Sujeto Obligado</Label>
              <Input
                value={reportForm.sujeto_obligado}
                onChange={(e) => setReportForm({ ...reportForm, sujeto_obligado: e.target.value })}
                placeholder="Nombre del sujeto obligado"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Descripción del Reporte *</Label>
              <Textarea
                value={reportForm.descripcion}
                onChange={(e) => setReportForm({ ...reportForm, descripcion: e.target.value })}
                placeholder="Describa los hechos y motivos del reporte..."
                rows={4}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateReport} disabled={!reportForm.titulo || !reportForm.fecha_operacion} style={{ backgroundColor: '#dc2626', color: 'white' }}>
              <FileText size={16} className="mr-2" />
              Crear Reporte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="text-blue-600" size={20} />
              Programar Envío de Reportes
            </DialogTitle>
            <DialogDescription>Configure el envío automático por email</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre de la Programación *</Label>
              <Input
                value={scheduleForm.nombre}
                onChange={(e) => setScheduleForm({ ...scheduleForm, nombre: e.target.value })}
                placeholder="Ej: Reporte Semanal Compliance"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Tipo de Reporte</Label>
              <Select value={scheduleForm.tipo_reporte} onValueChange={(v) => setScheduleForm({ ...scheduleForm, tipo_reporte: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="compliance">Dashboard Compliance</SelectItem>
                  <SelectItem value="sla">Dashboard SLAs</SelectItem>
                  <SelectItem value="executive">Dashboard Ejecutivo</SelectItem>
                  <SelectItem value="alerts">Reporte de Alertas</SelectItem>
                  <SelectItem value="cases">Reporte de Casos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Frecuencia</Label>
                <Select value={scheduleForm.frecuencia} onValueChange={(v) => setScheduleForm({ ...scheduleForm, frecuencia: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="diario">Diario</SelectItem>
                    <SelectItem value="semanal">Semanal</SelectItem>
                    <SelectItem value="quincenal">Quincenal</SelectItem>
                    <SelectItem value="mensual">Mensual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hora de Envío</Label>
                <Input
                  type="time"
                  value={scheduleForm.hora_envio}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, hora_envio: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Emails (separados por coma) *</Label>
              <Input
                value={scheduleForm.emails}
                onChange={(e) => setScheduleForm({ ...scheduleForm, emails: e.target.value })}
                placeholder="email1@empresa.com, email2@empresa.com"
                className="mt-1"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={scheduleForm.activo}
                onCheckedChange={(v) => setScheduleForm({ ...scheduleForm, activo: v })}
              />
              <Label>Programación activa</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveSchedule} disabled={!scheduleForm.nombre || !scheduleForm.emails}>
              <Calendar size={16} className="mr-2" />
              Guardar Programación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl" style={{ backgroundColor: '#ffffff' }}>
          {selectedReport && (
            <>
              <DialogHeader>
                <DialogTitle>Detalle del Reporte {selectedReport.id}</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-4">
                  {getStatusBadge(selectedReport.estado)}
                  <Badge style={{ backgroundColor: `${REPORT_TYPES.find(t => t.id === selectedReport.tipo)?.color}20`, color: REPORT_TYPES.find(t => t.id === selectedReport.tipo)?.color }}>
                    {selectedReport.tipo}
                  </Badge>
                </div>
                
                <div>
                  <Label className="text-slate-500">Título</Label>
                  <p className="font-medium text-slate-900">{selectedReport.titulo}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Monto Total</Label>
                    <p className="font-bold text-lg text-slate-900">{formatCurrency(selectedReport.monto_total)}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Fecha Operación</Label>
                    <p className="font-medium text-slate-900">{formatDate(selectedReport.fecha_operacion)}</p>
                  </div>
                </div>
                
                {selectedReport.caso_id && (
                  <div>
                    <Label className="text-slate-500">Caso Relacionado</Label>
                    <p className="font-mono text-sm text-blue-600">{selectedReport.caso_id}</p>
                  </div>
                )}
                
                <div>
                  <Label className="text-slate-500">Creado por</Label>
                  <p className="text-slate-900">{selectedReport.creado_por}</p>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Cerrar</Button>
                <Button variant="outline">
                  <Download size={16} className="mr-2" />
                  Descargar PDF
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportesRegulatorios;
